import React, { useEffect, useState, useCallback } from "react";
import "../index.css";
// Mismos badges (candado / pantalla) que la vista de plataforma.
import "../../door-badge.css";
import { MdLaptopChromebook } from "react-icons/md";
import { translate } from "../../../../../utils/Common";
import { FaLock, FaBuilding } from "react-icons/fa";
import NumberWithPercentage from "../../../../../components/NumberWithPercentage";
import { FaComputer } from "react-icons/fa6";
import ModalContent from "../../../../../components/ModalContent";
import { usePlatforms } from "../../../../../hooks/usePlatforms";
import AppleSpinner from "../../../../../components/AppleSpinner";
import { isSameDoor, moveDoorInModule } from "../doorOrder";

// Posición (doorSequence) de cada puerta, en la esquina inferior derecha, solo al editar puertas.
// En false la oculta.
const SHOW_DOOR_POSITION = true;

const PreviewLocker = (props) => {
  const {
    data,
    config,
    setConfig,
    setSelectDoor,
    selectDoor,
    doorsInBd,
    setOptionSelect,
    isLoadingDoor,
    isLoadingData,
    externalId,
    setModulesIsCompleted,
    editModeModule,
    editModeDoors,
    refreshAssignments,
    onDoorOrdersChange,
    canReorderDoors,
    pendingDoorOrderChanges,
  } = props;

  // puertas guardadas cuya posición cambia al guardar: su P se resalta
  const movedDoorIds = new Set(
    Object.values(pendingDoorOrderChanges || {})
      .flat()
      .filter((door) => door.oldSequence !== door.doorSequence)
      .map((door) => door.boxitDoorId)
  );

  const [showModal, setShowModal] = useState(false);
  const [modal] = useState({ title: '', body: '', footer: '' });
  const [doorAssignments, setDoorAssignments] = useState({});
  const [dragInfo, setDragInfo] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  
  const { getDoorCompanyAssignments } = usePlatforms();
  const isEditingExistingDoor = editModeDoors && selectDoor?.boxitDoorId > 0;

  const getClassDoorColor = (module, door) => {
    const isLoading = isLoadingDoor;

    if ((selectDoor?.id || selectDoor?.boxitDoorId) === (door?.id || door?.boxitDoorId)) {
      const isDraft = !door?.boxitDoorId || door?.boxitDoorId <= 0;
      const draftModifier = isDraft ? " module-door-selected-draft" : "";
      return "module-door-selected" + draftModifier + (isLoading ? " module-door-loading" : "");
    }

    if (door?.boxitDoorId > 0) {
      return "module-door-check " + (isLoading ? " module-door-loading" : "");
    }

    return "module-door-draft " + (isLoading ? " module-door-loading" : "");
  };

  // Estructura de solo vista para módulos en borrador (status 1) sin columnas generadas aún
  const getDisplayColumns = (module) => {
    if (module.columns && module.columns.length > 0) return module.columns;
    const numDoors = Number(module.numDoors) || 20;
    const numColumns = Number(module.numColumns) || 2;
    const startDoorNumber = Number(module.startDoorNumber) || 1;
    const doorsPerColumn = Math.floor(numDoors / numColumns);
    const remainder = numDoors % numColumns;
    const displayColumns = [];
    let doorCounter = startDoorNumber;
    let globalSequence = 1;
    for (let i = 0; i < numColumns; i++) {
      // Las puertas "extra" (remainder) se asignan a las últimas columnas
      // para que los borradores nuevos se generen al final (última columna).
      const doorsInThisColumn = doorsPerColumn + (i >= numColumns - remainder ? 1 : 0);
      const doors = [];
      for (let j = 0; j < doorsInThisColumn; j++) {
        doors.push({
          doorNumber: doorCounter++,
          doorSequence: globalSequence++,
          boxitDoorId: 0,
          dimension: "S",
          layer: "H",
          isActive: true,
          isScreen: false,
        });
      }
      displayColumns.push({ columnId: i + 1, doors });
    }
    return displayColumns;
  };

  // Función para verificar si un módulo está completamente lleno
  const isModuleComplete = useCallback((module) => {
    if (!module?.moduleId || !module?.numDoors) return false;
    
    // Contar puertas creadas en la base de datos
    const doorsInModule = doorsInBd.filter(
      (door) => door.moduleId === module.moduleId && door.locationId === module.locationId
    );
    const doorsInDatabase = doorsInModule.length;
    const totalDoorsInModule = module.numDoors;
    
    // El módulo está completo cuando el número de puertas creadas es igual al total de puertas
    return doorsInDatabase === totalDoorsInModule && totalDoorsInModule > 0;
  }, [doorsInBd]);

  const handleDragStart = (module, column, doorIndex, door) => {
    // mover una puerta reordena las demás del módulo: sin permiso de reordenar no se arrastra
    if (!editModeDoors || !canReorderDoors || !module?.moduleId || !setConfig) return;

    // Si estamos editando una puerta existente, solo permitir arrastrar la puerta seleccionada
    if (isEditingExistingDoor && !isSameDoor(selectDoor, door)) return;
    
    // Verificar que el módulo esté completamente lleno antes de permitir arrastrar
    if (!isModuleComplete(module)) return;

    setDragInfo({
      moduleId: module.moduleId,
      fromColumnId: column.columnId,
      fromIndex: doorIndex,
      doorId: door.id,
      boxitDoorId: door.boxitDoorId,
    });
  };

  const handleDrop = (module, column, doorIndex) => {
    if (!editModeDoors || !dragInfo || !setConfig || !module?.moduleId) return;
    
    // Verificar que el módulo esté completamente lleno antes de permitir soltar
    if (!isModuleComplete(module)) {
      setDragInfo(null);
      setDropTarget(null);
      return;
    }
    
    if (dragInfo.moduleId !== module.moduleId) {
      // Por ahora no soportamos mover entre módulos distintos
      setDragInfo(null);
      return;
    }

    const params = {
      moduleId: module.moduleId,
      fromColumnId: dragInfo.fromColumnId,
      fromIndex: dragInfo.fromIndex,
      toColumnId: column.columnId,
      toIndex: doorIndex,
    };

    setConfig((prevConfig) => {
      const { config: updatedConfig, changedRealDoors } = moveDoorInModule(prevConfig, params);
      if (updatedConfig === prevConfig) return prevConfig;

      // Mantener sincronizada la puerta seleccionada con sus nuevos valores
      const updatedModule = updatedConfig.modules.find((m) => m.moduleId === module.moduleId);
      const updatedDoor = selectDoor && updatedModule?.columns
        ?.flatMap((c) => c.doors || [])
        .find((d) => isSameDoor(d, selectDoor));
      if (updatedDoor) setSelectDoor(updatedDoor);

      // Notificar al padre qué puertas reales cambiaron de orden
      if (onDoorOrdersChange && changedRealDoors.length > 0) {
        onDoorOrdersChange(changedRealDoors);
      }

      return updatedConfig;
    });

    setDragInfo(null);
    setDropTarget(null);
  };

  const handleSelectDoor = (module, door) => {    
    // Si estamos editando una puerta ya creada, no permitir cambiar de puerta desde el preview
    if (isEditingExistingDoor) {
      return;
    }
    
    if(module.moduleId){
      const url = new URL(window.location.href);
      url.searchParams.set('opt', 'doors');
      window.history.pushState({}, '', url);
    
      // Usar un identificador único (puede ser door.doorSequence) para la puerta
      setSelectDoor((prevDoor) => {
        // Si la puerta seleccionada ya es la misma, la deseleccionamos
        return prevDoor?.doorSequence === door.doorSequence ? null : door;
      });
  
      setOptionSelect('doors');
    }
  };

  // Cargar asignaciones de empresas cuando cambie la plataforma o se refresque
  useEffect(() => {
    if (data?.IdPlataforma) {
      const loadAssignments = async () => {
        try {
          const abortController = new AbortController();
          const assignments = await getDoorCompanyAssignments(data.IdPlataforma, abortController.signal);
          const assignmentMap = {};
          assignments.forEach(assignment => {
            assignmentMap[assignment.doorId] = {
              companyCode: assignment.companyCode,
              companyName: assignment.companyName
            };
          });
          setDoorAssignments(assignmentMap);
        } catch (error) {
          console.error('Error loading door assignments:', error);
        }
      };
      loadAssignments();
    }
  }, [data?.IdPlataforma, getDoorCompanyAssignments, refreshAssignments]);

    // Función para calcular el porcentaje de ocupación de un módulo
    const calculateModuleProgress = useCallback((module) => {
      const doorsInModule = doorsInBd.filter(
        (door) => door.moduleId === module.moduleId && door.locationId === module.locationId
      );
      const doorsInDatabase = doorsInModule.length;
      const totalDoorsInModule = module.numDoors;
      const doorsInDatabasePercentage = (doorsInDatabase / totalDoorsInModule) * 100;
      return Math.min(doorsInDatabasePercentage, 100).toFixed(0);
    }, [doorsInBd]);

    useEffect(() => {
      if (config?.modules?.length > 0) {
        const allModulesComplete = config.modules.every(
          (module) => calculateModuleProgress(module) === "100"
        );
        
        if (allModulesComplete && !editModeModule && !editModeDoors) {
          // setModal({
          //   title: translate( "global.alert.ready"),
          //   body: translate("global.allModulesComplete"),
          //   footer: <div style={{ display: "flex", gap: "10px" }}>
          //       <button className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 border border-secondary bg-transparent text-secondary hover:bg-secondary hover:text-secondary-foreground" onClick={() => setShowModal(false)}>{translate('global.return')}</button>
          //       <button className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/90" onClick={() =>  window.location.href = `/dashboard/platforms/general`}><FaHome className="mr-1"/>{translate("global.goToHome")}</button>
          //   </div>,
          // });
          
          setModulesIsCompleted && setModulesIsCompleted(allModulesComplete);
        }
      }
    }, [config, calculateModuleProgress, editModeDoors, editModeModule, setModulesIsCompleted]);
    

  // Con módulos ya dibujados, la recarga (p. ej. al crear una puerta) los deja montados y pone el
  // spinner encima: cambiarlos por el spinner achicaba el contenido y el scroll horizontal volvía al inicio.
  const isReloading = isLoadingData || isLoadingDoor;
  const hasModules = config?.modules?.length > 0;

  return (
    <div className="cardInternal previewLocker-card" style={{ position: "relative" }}>
      {isReloading && hasModules && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50 animate-in fade-in-0 duration-150">
          <AppleSpinner size={40} />
        </div>
      )}
      <div className="cardInternal-body">
        {/* DATA DETAILS PLATFORM */}
        {data?.IdPlataforma && (
          <div>
            <span className="title-preview">{data?.namePlatform}</span> -{" "}
            <span className="title-num-preview">
              {data?.IdPlataforma ? "ID. " + data?.externalId : " "}
            </span>
          </div>
        )}
        {isReloading && !hasModules ? (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '3rem'
          }}>
            <AppleSpinner size={40} />
          </div>
        ) : config?.modules?.length > 0 ? (
          <div className="modules-container">
            {config.modules.map((module, moduleIndex) => {
              const moduleProgress = calculateModuleProgress(module); 
              return(
              <div key={moduleIndex} className="module-wrapper">
                <div className="flex mb-2" style={{ alignItems: "center", gap: "10px" }}>
                {
                  moduleProgress > 0 && (
                    <div className="module-progress">
                    <NumberWithPercentage number={moduleProgress} /> 
                  </div>
                  )
                }
                 
                
                <div className="module-title">
                    {translate("global.module")}{" "}
                    <span className="font-semibold">{module.moduleNumber}</span>
                  </div>

                  {
                  module.isUnitCentral && (
                    <div className="circle-gray-icon">
                      <FaComputer  size={18} color='#47fa7f'/>
                    </div>
                  )
                }
                </div>

               
                 
                <div
                  className={
                    module.status === 1
                      ? "module-container-draft"
                      : "module-container"
                  }
                >
                  <div className="columns-container">
                    {getDisplayColumns(module).map((column, columnIndex) => (
                      <div key={columnIndex} className="column">
                        {column.doors.map((door, doorIndex) => {
                          const isBeforeTarget =
                            dropTarget &&
                            dropTarget.moduleId === module.moduleId &&
                            dropTarget.columnId === column.columnId &&
                            dropTarget.doorIndex === doorIndex;

                          const showSeparatorsForModule =
                            !!dragInfo && dragInfo.moduleId === module.moduleId;

                          const isSelectedDoor = isSameDoor(selectDoor, door);

                          return (
                            <React.Fragment
                              key={
                                door.id ||
                                `v-${moduleIndex}-${columnIndex}-${door.doorSequence}`
                              }
                            >
                              {/* Separador entre puertas: espaciador siempre; hitbox solo en editar */}
                              {editModeDoors ? (
                                <div
                                  className="door-drop-hitbox"
                                  onDragOver={(e) => {
                                    if (editModeDoors && module.moduleId && setConfig && isModuleComplete(module)) {
                                      e.preventDefault();
                                      setDropTarget({
                                        moduleId: module.moduleId,
                                        columnId: column.columnId,
                                        doorIndex,
                                      });
                                    }
                                  }}
                                  onDragLeave={(e) => {
                                    if (!e.currentTarget.contains(e.relatedTarget)) {
                                      setDropTarget(null);
                                    }
                                  }}
                                  onDrop={() => handleDrop(module, column, doorIndex)}
                                >
                                  <div
                                    className={`door-drop-line ${
                                      showSeparatorsForModule
                                        ? "door-drop-line-visible"
                                        : ""
                                    } ${
                                      isBeforeTarget
                                        ? "door-drop-line-active"
                                        : ""
                                    }`}
                                  />
                                </div>
                              ) : (
                                <div className="door-drop-spacer" />
                              )}

                              {/* Tarjeta de puerta */}
                              <div
                                className={getClassDoorColor(module, door)}
                                onClick={() =>
                                  !isLoadingDoor && handleSelectDoor(module, door)
                                }
                                style={{
                                  position: "relative", // ancla la posición en la esquina inferior derecha
                                  cursor:
                                    isEditingExistingDoor && !isSelectedDoor
                                      ? "not-allowed"
                                      : module.moduleId && editModeDoors && canReorderDoors && isModuleComplete(module)
                                      ? "grab"
                                      : module.moduleId
                                      ? "pointer"
                                      : "not-allowed",
                                }}
                                draggable={!!(
                                  editModeDoors &&
                                  canReorderDoors &&
                                  module.moduleId &&
                                  setConfig &&
                                  isModuleComplete(module) &&
                                  (!isEditingExistingDoor || isSelectedDoor)
                                )}
                                onDragStart={() =>
                                  handleDragStart(module, column, doorIndex, door)
                                }
                              >
                                {SHOW_DOOR_POSITION && editModeDoors && door?.doorSequence != null && (
                                  <span
                                    className={`pointer-events-none absolute bottom-1 right-1.5 text-[9px] font-medium leading-none ${
                                      movedDoorIds.has(door.boxitDoorId)
                                        ? "rounded bg-white px-1 py-0.5 text-[#1f2937] shadow-sm"
                                        : "opacity-75"
                                    }`}
                                    title="doorSequence"
                                  >
                                    P{door.doorSequence}
                                  </span>
                                )}
                                {/* Mismo patrón que la vista de plataforma
                                    (SmartLocker/LockerDoor): fila flex con gap
                                    y badges `door-badge`. Antes eran spans en
                                    línea con padding, así que el padding se
                                    salía de la caja de texto y el borde de la
                                    puerta recortaba el candado y la pantalla. */}
                                <div className="flex min-w-0 items-start gap-1.5 self-start leading-none">
                                  <span>{door.doorNumber}</span>
                                  {!door?.isActive && (
                                    <span
                                      className="door-badge"
                                      style={{ background: "rgba(255,255,255,0.22)" }}
                                      title={translate("global.inactive")}
                                    >
                                      <FaLock size={11} />
                                    </span>
                                  )}
                                  {door?.isScreen && (
                                    <span
                                      className="door-badge"
                                      style={{ background: "#0058ff" }}
                                      title="Screen"
                                    >
                                      <MdLaptopChromebook size={13} />
                                    </span>
                                  )}
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-end",
                                  }}
                                >
                                  <div>
                                    {door?.isScreen
                                      ? "C - "
                                      : door?.dimension && `${door.dimension} - `}{" "}
                                    {door.layer}
                                  </div>
                                  {doorAssignments[door.boxitDoorId]?.companyName && (
                                    <div
                                      style={{
                                        background:
                                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                        padding: "2px 6px",
                                        borderRadius: "12px",
                                        fontSize: "8px",
                                        color: "white",
                                        fontWeight: "600",
                                        letterSpacing: "0.2px",
                                        textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                                        boxShadow:
                                          "0 2px 4px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)",
                                        border: "1px solid rgba(255,255,255,0.3)",
                                        backdropFilter: "blur(12px)",
                                        transition: "all 0.3s ease-in-out",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "3px",
                                        minWidth: "fit-content",
                                        maxWidth: "80px",
                                        marginTop: "4px",
                                        zIndex: 10,
                                      }}
                                      title={`${translate("global.business")}: ${
                                        doorAssignments[door.boxitDoorId].companyName
                                      }`}
                                    >
                                      <FaBuilding
                                        size={7}
                                        style={{ opacity: 0.95 }}
                                      />
                                      <span>
                                        {doorAssignments[door.boxitDoorId].companyName
                                          .length > 8
                                          ? `${doorAssignments[door.boxitDoorId].companyName.substring(
                                              0,
                                              8
                                            )}...`
                                          : doorAssignments[door.boxitDoorId].companyName}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </React.Fragment>
                          );
                        })}

                        {/* Separador al final de la columna: espaciador siempre; hitbox solo en editar */}
                        {editModeDoors ? (
                          <div
                            className="door-drop-hitbox"
                            onDragOver={(e) => {
                              if (editModeDoors && module.moduleId && setConfig && isModuleComplete(module)) {
                                e.preventDefault();
                                setDropTarget({
                                  moduleId: module.moduleId,
                                  columnId: column.columnId,
                                  doorIndex: column.doors.length,
                                });
                              }
                            }}
                            onDragLeave={(e) => {
                              if (!e.currentTarget.contains(e.relatedTarget)) {
                                setDropTarget(null);
                              }
                            }}
                            onDrop={() =>
                              handleDrop(module, column, column.doors.length)
                            }
                          >
                            <div
                              className={`door-drop-line ${
                                dragInfo && dragInfo.moduleId === module.moduleId
                                  ? "door-drop-line-visible"
                                  : ""
                              } ${
                                dropTarget &&
                                dropTarget.moduleId === module.moduleId &&
                                dropTarget.columnId === column.columnId &&
                                dropTarget.doorIndex === column.doors.length
                                  ? "door-drop-line-active"
                                  : ""
                              }`}
                            />
                          </div>
                        ) : (
                          <div className="door-drop-spacer" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )})}
          </div>
        ) : (
          <div className="messageAlert">
            {translate("global.messageNoPlatformModuleCreated")}
          </div>
        )}
      </div>
      {showModal && (
<ModalContent
title={modal.title}
body={modal.body}
footer={modal.footer}
isOpenModal={showModal}
onHide={() => setShowModal(false)}
/>
      )}
    </div>
  );
};

export default PreviewLocker;
